import React, { useEffect, useState } from 'react';
import { Calendar, Sparkles } from 'lucide-react';
import { api } from '../context/AppContext';
import { Button } from './ui/button';

/**
 * CycleProgressBadge — small widget for `MyPlanTab.js` (Iter. 405.j).
 *
 * Shows "Cycle 1 — Jour 23/42" plus a CTA when reassessment is due.
 * Polls `/training-cycles/current` on mount; cheap, cached server-side.
 */
export default function CycleProgressBadge({ language = 'fr', onReassessClick }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/training-cycles/current')
      .then((r) => { if (alive) setState(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!state || !state.cycle) return null;
  const fr = language === 'fr';
  const { cycle, days_elapsed, days_remaining, reassessment_due } = state;
  const total = cycle.duration_days || 42;
  const pct = Math.min(100, Math.max(0, ((days_elapsed || 0) / total) * 100));

  return (
    <div
      data-testid="cycle-progress-badge"
      className="rounded-xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 p-3 mb-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
          <span className="text-xs font-semibold text-foreground">
            {fr ? `Cycle ${cycle.cycle_number}` : `Cycle ${cycle.cycle_number}`} —{' '}
            <span className="text-violet-400">
              {fr ? `Jour ${days_elapsed}/${total}` : `Day ${days_elapsed}/${total}`}
            </span>
          </span>
        </div>
        {reassessment_due && (
          <Button
            onClick={onReassessClick}
            size="sm"
            className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs h-7"
            data-testid="cycle-reassess-cta"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {fr ? 'Bilan IA' : 'AI review'}
          </Button>
        )}
      </div>
      <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {!reassessment_due && (
        <p className="text-[10px] text-muted-foreground mt-1.5">
          {fr ? `${days_remaining} jours restants avant ton prochain bilan IA` : `${days_remaining} days left before your next AI review`}
        </p>
      )}
    </div>
  );
}
